import { Controller } from 'react-hook-form'
import React from 'react'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'

import { cn } from 'src/utils'
import { InputType } from 'src/types'
import { DATE_FORMAT } from 'src/constants'

import { getIsFutureDate } from 'src/helpers/getIsFutureDate'
import { ButtonNavigateToCars } from 'src/features/ButtonNavigateToCars'
import Container from 'src/components/Container'
import AppBreadcrumb from 'src/components/AppBreadcrumb'

import { useFuelTracking } from 'src/pages/FuelTracking/useFuelTracking'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/shadcn/popover'
import { Input } from 'src/components/shadcn/input'
import { Card, CardContent, CardHeader } from 'src/components/shadcn/card'
import { Calendar } from 'src/components/shadcn/calendar'
import { Button } from 'src/components/shadcn/button'

const FuelTracking = (): JSX.Element => {
  const {
    locale,
    breadcrumbItems,
    formattedTitle,
    onSubmit,
    handleSubmit,
    control,
    setValue,
    errors,
    fields,
    fuelTypeOptions,
    isLoadingAddFuelHistory,
  } = useFuelTracking()

  return (
    <Container>
      <AppBreadcrumb items={breadcrumbItems} />
      <div className="flex items-center justify-center w-full">
        <Card className="w-full md:w-2/4 mt-8 rounded-xl">
          <CardHeader className="p-6 border-b mb-2">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold">{locale.pageTitle}</h1>
              <ButtonNavigateToCars />
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <h1 className="text-lg font-medium mb-2">
                  {locale.serviceDate}
                </h1>
                <Controller
                  name="date"
                  control={control}
                  rules={{
                    required: locale.dateRequired,
                    validate: value =>
                      getIsFutureDate(value) || locale.dateRequired,
                  }}
                  render={({ field }) => (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-60 justify-start text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          <CalendarIcon />
                          {field.value ? (
                            format(field.value, DATE_FORMAT)
                          ) : (
                            <span>{locale.serviceDatePlaceholder}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto rounded-md">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={date =>
                            setValue('date', date ?? field.value)
                          }
                          initialFocus
                          toDate={new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />
                {errors.date && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.date.message}
                  </p>
                )}
              </div>

              <div>
                {fields.map(
                  ({ name, inputType, title, placeHolder, isReadOnly }) =>
                    inputType === InputType.SELECT ? (
                      <div key={name}>
                        <h1 className="mt-5 text-lg font-medium mb-2">
                          {formattedTitle(name, title)}
                        </h1>
                        <Controller
                          name={name}
                          control={control}
                          rules={{ required: locale.requiredField }}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={placeHolder} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>{title}</SelectLabel>
                                  {fuelTypeOptions.map(({ value, label }) => (
                                    <SelectItem key={value} value={value}>
                                      {label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                    ) : (
                      <div key={name}>
                        <h1 className="mt-5 text-lg font-medium mb-2">
                          {formattedTitle(name, title)}
                        </h1>
                        <Controller
                          name={name}
                          control={control}
                          rules={{ required: locale.requiredField }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id={name}
                              type={inputType}
                              placeholder={placeHolder}
                              readOnly={isReadOnly}
                            />
                          )}
                        />
                        {errors[name] && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors[name]?.message}
                          </p>
                        )}
                      </div>
                    ),
                )}
              </div>

              <div className="!mt-5 flex items-center justify-center">
                <Button
                  disabled={isLoadingAddFuelHistory}
                  className="w-[100px] rounded-[10px]"
                  type="submit"
                >
                  {locale.button}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Container>
  )
}

export default FuelTracking
